import type { ApplicationTypeEnumType } from "@libs/api/patients";
import type { I18nKeyType } from "@/i18n";

import {
  MARKETPLACE_SELF_ENROLLMENT_URL,
  MEDICARE_SELF_ENROLLMENT_URL,
} from "./constants";

// The prototype draws three program combinations, but its variants are really
// the product of two independent axes: which programs need an agent, and whether
// Medicaid is also in the mix. Deriving them separately covers every combination
// by construction, including the single-program ones the prototype omits.

export type AgentAssistedProgramType = Extract<
  ApplicationTypeEnumType,
  "MEDICARE" | "MARKETPLACE"
>;

export type BrokerConsentAnswerType = "yes" | "no";

export const SELF_ENROLLMENT_URL_BY_PROGRAM: Record<
  AgentAssistedProgramType,
  string
> = {
  MEDICARE: MEDICARE_SELF_ENROLLMENT_URL,
  MARKETPLACE: MARKETPLACE_SELF_ENROLLMENT_URL,
};

export type BrokerConsentCopyType = {
  agentAssistedPrograms: AgentAssistedProgramType[];
  declineFallbackI18nKey: I18nKeyType;
  declineContinueButtonI18nKey: I18nKeyType;
  submissionSummaryI18nKey: I18nKeyType;
};

// Medicare leads so the composed label reads "Medicare and ACA Marketplace".
const AGENT_ASSISTED_PROGRAMS = [
  "MEDICARE",
  "MARKETPLACE",
] as const satisfies readonly AgentAssistedProgramType[];

export const getBrokerConsentCopy = (
  applicationTypes: readonly ApplicationTypeEnumType[],
): BrokerConsentCopyType | null => {
  const agentAssistedPrograms = AGENT_ASSISTED_PROGRAMS.filter(program =>
    applicationTypes.includes(program),
  );

  if (agentAssistedPrograms.length === 0) {
    return null;
  }

  const hasMedicaid = applicationTypes.includes("MEDICAID");

  return {
    agentAssistedPrograms,
    declineFallbackI18nKey: hasMedicaid
      ? "native:brokerConsentScreen.declineCard.fallbackWithMedicaid"
      : "native:brokerConsentScreen.declineCard.fallbackWithoutMedicaid",
    declineContinueButtonI18nKey: hasMedicaid
      ? "native:brokerConsentScreen.continueWithMedicaidOnlyButton"
      : "native:brokerConsentScreen.continueWithoutAgentButton",
    submissionSummaryI18nKey: hasMedicaid
      ? "native:applicationConfirmationScreen.brokerDeclined.summaryWithMedicaid"
      : "native:applicationConfirmationScreen.brokerDeclined.summaryWithoutMedicaid",
  };
};
